import {useEffect, useState} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {Alert, Button, DatePicker, InputNumber, Spin} from 'antd';
import {LineChartOutlined} from '@ant-design/icons';
import moment from 'moment';
import PropTypes from 'prop-types';

import DBModal from 'components/modal/modal';
import {BULK_UPDATE_SWAP_RATE} from 'graphql/mutations/wallet';
import {
  GET_PUBLIC_EXCHANGE_RATES,
  GET_SWAP_TRANSACTION_SUMMARY,
} from 'graphql/queries/wallet';
import {formatRate, formatRateValue, symbolFor} from 'utils/currencyFormatting';
import {
  buildSwapFilters,
  describeRateSelection,
  describeSwapFilters,
  MAX_RATE_ALL_MATCHING,
  MAX_RATE_SELECTED_IDS,
  submitBulkSwapRate,
} from './swapReporting';
import styles from './swapTransactions.module.scss';

const plural = (count, word) =>
  `${count.toLocaleString('en-US')} ${word}${count === 1 ? '' : 's'}`;

/**
 * Writes one rate to many pending swaps at once. Nothing is settled: each
 * swap stays pending with its received amount recalculated, and fulfilling
 * it later credits the user at the new rate.
 *
 * `request` is `{ids, rows}` for ticked rows — `rows` are the records behind
 * those ids, used to check they share one currency pair — or
 * `{allMatching: true, currencies}` for every pending swap of the pair the
 * page is filtered to, optionally narrowed to dates picked here. The backend
 * runs this inline and answers with the full outcome, which is shown in place.
 */
const SwapBulkRateModal = ({request, onClose, onDone}) => {
  const [rate, setRate] = useState(null);
  const [dates, setDates] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [mutate] = useMutation(BULK_UPDATE_SWAP_RATE);

  useEffect(() => {
    if (request) {
      setRate(null);
      setDates(undefined);
      setSubmitting(false);
      setResult(null);
      setError(null);
    }
  }, [request]);

  const selection = request?.ids ? describeRateSelection(request.rows) : null;
  const pair = request?.allMatching ? request.currencies : selection?.pair;
  const source = pair?.sourceCurrency;
  const target = pair?.targetCurrency;
  const pairKnown = Boolean(source && target);

  const allFilters =
    request?.allMatching && pairKnown
      ? buildSwapFilters({
          sourceCurrency: source,
          targetCurrency: target,
          dates,
        })
      : null;
  const {data: countData, loading: counting} = useQuery(
    GET_SWAP_TRANSACTION_SUMMARY,
    {
      variables: {input: allFilters},
      skip: !allFilters,
      fetchPolicy: 'no-cache',
    },
  );
  const countResult = countData?.getSwapTransactionSummary;
  const expected =
    countResult?.__typename === 'SwapTransactionSummary'
      ? countResult.pending.count
      : undefined;

  const {data: rateData, loading: loadingRates} = useQuery(
    GET_PUBLIC_EXCHANGE_RATES,
    {
      variables: {from: source, to: [target]},
      skip: !pairKnown,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
  );
  const marketRate = rateData?.getPublicFxExchangeRates?.to?.[0]?.rate;
  const lastUpdated = rateData?.getPublicFxExchangeRates?.lastUpdated;

  if (!request) return null;

  const selectedCount = request.ids?.length || 0;
  const count = request.ids ? selectedCount : expected;
  const tooManySelected = selectedCount > MAX_RATE_SELECTED_IDS;
  const tooManyMatching =
    !request.ids &&
    typeof expected === 'number' &&
    expected > MAX_RATE_ALL_MATCHING;
  const rateValue = Number(rate);
  const rateValid = Number.isFinite(rateValue) && rateValue > 0;
  const blocked =
    tooManySelected || tooManyMatching || selection?.mixed || !pairKnown;

  const start = async () => {
    if (blocked || !rateValid || submitting) return;
    setSubmitting(true);
    setError(null);
    const outcome = await submitBulkSwapRate({
      mutate,
      input: {
        rate: rateValue,
        ...(request.ids
          ? {ids: request.ids}
          : {allMatching: true, filters: {...allFilters, status: 'PENDING'}}),
      },
    });
    setSubmitting(false);
    if (outcome.result) {
      setResult(outcome.result);
      onDone?.(outcome.result);
    } else {
      setError(outcome.error);
    }
  };

  const currentRates =
    selection && selection.minRate != null
      ? selection.minRate === selection.maxRate
        ? formatRate(selection.minRate, source, target)
        : `${formatRateValue(selection.minRate)} – ${formatRate(
            selection.maxRate,
            source,
            target,
          )}`
      : null;

  return (
    <DBModal
      isOpen
      width={560}
      destroyOnClose
      handleClose={submitting ? undefined : onClose}
      content={
        <div className={styles.bulkModal}>
          <h2>
            Update rate on {request.ids ? 'selected swaps' : 'pending swaps'}
          </h2>

          {!result && (
            <>
              <p className={styles.bulkScope}>
                Sets one rate on{' '}
                <strong>
                  {request.ids
                    ? `${plural(selectedCount, 'selected pending swap')}${
                        pairKnown ? ` (${source} → ${target})` : ''
                      }`
                    : `every pending swap ${describeSwapFilters({
                        sourceCurrency: source,
                        targetCurrency: target,
                        dates,
                      })}`}
                </strong>
                {!request.ids
                  ? counting
                    ? ' — counting…'
                    : typeof expected === 'number'
                    ? ` — ${expected.toLocaleString(
                        'en-US',
                      )} right now, including ones on other pages.`
                    : '.'
                  : '.'}{' '}
                Each swap stays pending; the amount its user receives is
                recalculated and paid out at this rate when the swap is
                fulfilled.
              </p>

              {currentRates && (
                <p className={styles.bulkFieldHint}>
                  Current rate
                  {selection.minRate === selection.maxRate ? '' : 's'}:{' '}
                  {currentRates}
                </p>
              )}

              {!request.ids && pairKnown && (
                <div className={styles.bulkField}>
                  <label htmlFor="swap-bulk-rate-dates">
                    Created between (optional)
                  </label>
                  <DatePicker.RangePicker
                    id="swap-bulk-rate-dates"
                    allowEmpty={[true, true]}
                    format="D MMM YYYY"
                    placeholder={['Any start', 'Any end']}
                    value={dates}
                    onChange={setDates}
                  />
                  <span className={styles.bulkFieldHint}>
                    Leave empty to include pending swaps from every date.
                  </span>
                </div>
              )}

              {pairKnown &&
                (loadingRates ? (
                  <div className={styles.marketRate}>
                    <Spin size="small" />
                    <span className={styles.marketRateHint}>
                      Fetching market rate…
                    </span>
                  </div>
                ) : (
                  marketRate != null && (
                    <div className={styles.marketRate}>
                      <LineChartOutlined />
                      <div className={styles.marketRateBody}>
                        <span className={styles.marketRateLabel}>
                          Live market rate
                        </span>
                        <span className={styles.marketRateValue}>
                          {formatRate(marketRate, source, target)}
                        </span>
                        {lastUpdated && (
                          <span className={styles.marketRateHint}>
                            Updated{' '}
                            {moment(lastUpdated).format('DD MMM YYYY, h:mm A')}
                          </span>
                        )}
                      </div>
                    </div>
                  )
                ))}

              {!blocked && (
                <div className={styles.bulkField}>
                  <label htmlFor="swap-bulk-rate">
                    New rate — {target} per 1 {source}
                  </label>
                  <InputNumber
                    id="swap-bulk-rate"
                    className={styles.fullWidthInput}
                    min={0}
                    step={0.0001}
                    stringMode
                    prefix={symbolFor(target)}
                    value={rate}
                    placeholder={
                      marketRate != null
                        ? formatRateValue(marketRate)
                        : undefined
                    }
                    onChange={setRate}
                  />
                  {marketRate != null && (
                    <Button
                      type="link"
                      className={styles.inlineLink}
                      onClick={() => setRate(String(marketRate))}>
                      Use the market rate ({formatRateValue(marketRate)})
                    </Button>
                  )}
                </div>
              )}
            </>
          )}

          {selection?.mixed && (
            <Alert
              type="error"
              showIcon
              message="The selected swaps use different currency pairs."
              description="One rate can only apply to one pair. Select swaps for a single pair, or filter the page to the pair and use Bulk update pending."
            />
          )}

          {request.ids && !selection?.mixed && !pairKnown && (
            <Alert
              type="error"
              showIcon
              message="The currency pair of the selected swaps could not be read. Clear the selection and tick them again."
            />
          )}

          {!request.ids && !pairKnown && (
            <Alert
              type="error"
              showIcon
              message="Filter the page to one source and one target currency first."
            />
          )}

          {tooManySelected && (
            <Alert
              type="error"
              showIcon
              message={`At most ${MAX_RATE_SELECTED_IDS} swaps can be selected for a rate update. Filter the page to the pair and use Bulk update pending → Update rate instead.`}
            />
          )}

          {tooManyMatching && (
            <Alert
              type="error"
              showIcon
              message={`This scope holds ${expected.toLocaleString(
                'en-US',
              )} pending swaps; at most ${MAX_RATE_ALL_MATCHING.toLocaleString(
                'en-US',
              )} can be updated at once. Narrow the dates.`}
            />
          )}

          {error && (
            <Alert
              type="error"
              showIcon
              message="The rate was not updated"
              description={`${error} Nothing has changed, so you can try again.`}
            />
          )}

          {result && (
            <div className={styles.jobDetails}>
              <Alert
                type={result.failedCount ? 'warning' : 'success'}
                showIcon
                message={`Rate set to ${formatRate(
                  result.rate,
                  source,
                  target,
                )} on ${result.updated.toLocaleString('en-US')} of ${plural(
                  result.totalMatched,
                  'swap',
                )}.`}
              />
              {result.failedCount > 0 && (
                <div className={styles.bulkFailures}>
                  <strong>
                    Not updated ({result.failedCount.toLocaleString('en-US')})
                  </strong>
                  <ul>
                    {result.failed.map(item => (
                      <li key={item.id}>
                        <code>{item.id}</code> — {item.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className={styles.bulkActions}>
            {result ? (
              <Button type="primary" onClick={onClose}>
                Close
              </Button>
            ) : (
              <>
                <Button onClick={onClose} disabled={submitting}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  loading={submitting}
                  disabled={
                    blocked ||
                    !rateValid ||
                    (!request.ids && (counting || !expected))
                  }
                  onClick={start}>
                  Set rate on{' '}
                  {typeof count === 'number'
                    ? plural(count, 'swap')
                    : 'pending swaps'}
                </Button>
              </>
            )}
          </div>
        </div>
      }
    />
  );
};

SwapBulkRateModal.propTypes = {
  request: PropTypes.object,
  onClose: PropTypes.func,
  onDone: PropTypes.func,
};

export default SwapBulkRateModal;
